import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * Public rotor page — picks a single random link from the rotor on every
 * page load (server-side). Refreshing the page is the only way to rotate;
 * there is no auto-rotation timer. The branded ad bar at the top mirrors
 * the splash page style and credits the rotor owner on any new signups.
 */
export default function PublicRotor() {
  const { slug } = useParams();
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  // Bump this to force a re-fetch (and therefore a fresh random pick) on
  // the "Load another site" button without doing a full page reload.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: resp } = await api.get(`/rotors/public/${encodeURIComponent(slug)}`);
        if (!alive) return;
        setData(resp);
        setStatus("ok");
      } catch (e) {
        if (!alive) return;
        setStatus(e?.response?.status === 404 ? "missing" : "error");
      }
    })();
    return () => { alive = false; };
  }, [slug, reloadKey]);

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">Loading rotor…</div>;
  }
  if (status === "missing") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white p-6 text-center">
        <div className="text-3xl font-black">Rotor not found</div>
        <p className="mt-2 text-white/70">This rotor doesn't exist, has been paused, or no longer has any links.</p>
        <Link to="/" className="mt-4 text-blue-400 hover:underline text-sm">Go home →</Link>
      </div>
    );
  }
  if (status === "error" || !data) {
    return <div className="min-h-screen flex items-center justify-center text-red-500 bg-slate-950">Failed to load rotor.</div>;
  }

  const current = data.current;

  return (
    <div className="h-screen w-full flex flex-col bg-slate-950">
      {/* Branded ad bar — matches the splash page bar so members get the same
          free-signup pitch and credit the rotor owner on any new accounts. */}
      <a
        data-testid="rotor-ad-bar"
        href={data.owner_referral_code ? `/register?ref=${encodeURIComponent(data.owner_referral_code)}` : "/"}
        className="group relative block w-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white text-center py-1.5 sm:py-2 px-4 hover:from-black hover:to-black transition-colors border-b-2 border-indigo-400 shadow-[0_3px_0_rgba(129,140,248,0.35)]"
        style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
      >
        <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
          <span className="text-indigo-400 text-lg sm:text-xl md:text-2xl font-black tracking-tight drop-shadow-[0_2px_0_rgba(0,0,0,0.5)] leading-none">
            Trafficexchange.app
          </span>
          <span className="hidden sm:inline text-indigo-400/40 text-xl leading-none">•</span>
          <span className="text-white text-base sm:text-lg md:text-xl font-extrabold tracking-tight leading-none">
            Get your own <span className="underline decoration-indigo-400 decoration-4 underline-offset-2">FREE</span> site rotator here!
          </span>
          <span aria-hidden="true" className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-indigo-400 text-slate-900 text-base font-black group-hover:translate-x-1 transition-transform">
            →
          </span>
        </div>
      </a>

      {/* Secondary strip — rotor name, link count, and a shuffle button that
          forces another server-side random pick. */}
      <div className="w-full bg-slate-900 border-b border-slate-800 text-white text-xs sm:text-sm px-3 sm:px-4 py-1.5 flex items-center gap-3">
        <div className="font-black truncate" data-testid="rotor-name">{data.name}</div>
        <div className="text-white/60 shrink-0" data-testid="rotor-count">
          random pick from {data.link_count} link{data.link_count !== 1 ? "s" : ""}
        </div>
        <div className="text-white/50 truncate hidden sm:block flex-1">{current.label || current.url}</div>
        <button
          data-testid="rotor-shuffle"
          onClick={() => setReloadKey((k) => k + 1)}
          className="ml-auto px-3 py-0.5 text-xs rounded-full bg-indigo-500 hover:bg-indigo-400 font-black shrink-0"
        >
          Load another site ↻
        </button>
      </div>

      {/* Iframe fills the rest. */}
      <iframe
        data-testid="rotor-iframe"
        key={`${reloadKey}-${current.url}`}
        src={current.url}
        title="rotor-frame"
        className="flex-1 w-full border-0 bg-white"
        sandbox="allow-scripts allow-same-origin allow-forms"
      />
    </div>
  );
}
